'use client';

/**
 * RealtimeTile - Displays live call context search results
 *
 * Shows real-time information including:
 * - Call context snippets
 * - Suggested actions
 * - Attachments
 * - Time-sensitive indicators
 */

import {
  Zap,
  MessageSquare,
  Lightbulb,
  Paperclip,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { createTile, type BaseTileRenderProps } from './BaseTile';
import type { SearchResult } from '../types';

/**
 * Extended realtime result with specific metadata
 */
interface RealtimeResult extends SearchResult {
  type: 'realtime';
  metadata: {
    /** Type of realtime content */
    contentType?: 'transcript' | 'suggestion' | 'attachment';
    /** Timestamp in call */
    callTimestamp?: string;
    /** Confidence level for suggestions */
    confidence?: number;
    /** Action type for suggestions */
    actionType?: string;
    /** File info for attachments */
    fileName?: string;
    fileType?: string;
  };
}

/**
 * Get icon based on realtime content type
 */
function getRealtimeIcon(contentType?: string) {
  switch (contentType) {
    case 'transcript':
      return MessageSquare;
    case 'suggestion':
      return Lightbulb;
    case 'attachment':
      return Paperclip;
    default:
      return Zap;
  }
}

/**
 * Get icon color based on content type
 */
function getIconColor(contentType?: string): string {
  switch (contentType) {
    case 'transcript':
      return 'text-blue-400';
    case 'suggestion':
      return 'text-amber-400';
    case 'attachment':
      return 'text-slate-400';
    default:
      return 'text-rose-400';
  }
}

/**
 * Confidence indicator
 */
function ConfidenceIndicator({ confidence }: { confidence?: number }) {
  if (confidence === undefined) return null;

  const percentage = Math.round(confidence * 100);

  let colorClass = 'bg-slate-500';
  if (percentage >= 80) colorClass = 'bg-emerald-500';
  else if (percentage >= 60) colorClass = 'bg-amber-500';

  return (
    <div className="flex items-center gap-1.5">
      <div className="w-12 h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full', colorClass)}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs text-slate-500">{percentage}%</span>
    </div>
  );
}

/**
 * Live indicator badge
 */
function LiveBadge() {
  return (
    <Badge
      variant="outline"
      className="text-xs gap-1 bg-rose-500/10 text-rose-400 border-rose-500/30 animate-pulse"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
      Live
    </Badge>
  );
}

/**
 * RealtimeTile component
 */
export const RealtimeTile = createTile<RealtimeResult>(
  'RealtimeTile',
  (result) => {
    const { metadata } = result;
    const Icon = getRealtimeIcon(metadata.contentType);

    const renderProps: BaseTileRenderProps = {
      icon: Icon,
      iconColor: getIconColor(metadata.contentType),
      title: result.title,
      subtitle: metadata.callTimestamp
        ? `At ${metadata.callTimestamp}`
        : undefined,
      preview: result.preview,
      metadata: [],
      tags: [],
      rightContent: (
        <div className="flex items-center gap-2">
          {metadata.confidence !== undefined && (
            <ConfidenceIndicator confidence={metadata.confidence} />
          )}
          <LiveBadge />
        </div>
      ),
    };

    // Add content type tag
    if (metadata.contentType) {
      renderProps.tags = [metadata.contentType];
    }

    // Add action button for suggestions
    if (metadata.contentType === 'suggestion' && metadata.actionType) {
      renderProps.bottomContent = (
        <Button
          size="sm"
          variant="outline"
          className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20"
        >
          {metadata.actionType}
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      );
    }

    // Add file info for attachments
    if (metadata.contentType === 'attachment' && metadata.fileName) {
      renderProps.metadata!.push({
        label: 'File',
        value: metadata.fileName,
        icon: Paperclip,
      });
    }

    return renderProps;
  }
);

export default RealtimeTile;
